import { TreasuryError, type TreasuryOverviewPort, type TreasuryPort, type TreasuryPurchaseStep, type TreasurySnapshot, type TreasuryWalletRequest } from "../domain/treasury";
import { TREASURY_TRIAL_ROOT as trialRoot, TREASURY_ENVIRONMENT, TREASURY_PRODUCTION_ROOT } from "../config/treasuryTrial";

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const units = (value: unknown): value is string => typeof value === "string" && /^(0|[1-9][0-9]{0,59})$/.test(value);
const basisPoints = (value: unknown): value is number => Number.isSafeInteger(value) && Number(value) >= 0 && Number(value) <= 10_000;
const requestIdPattern = /^[A-Za-z0-9:_-]{8,100}$/;

function walletRequest(value: unknown, phase: "reserve" | "arm" | "deposit" | "cancel", serviceAdvanceNano?: string): TreasuryWalletRequest {
  // PROD only accepts the new one-confirmation protocol, never old TEST steps.
  if (TREASURY_ENVIRONMENT === "production" && (phase !== "deposit" || serviceAdvanceNano !== undefined)) throw new TreasuryError("UNAVAILABLE");
  const serviced = phase === "deposit" && serviceAdvanceNano !== undefined;
  if (!record(value) || value.network !== "-239" || typeof value.from !== "string" ||
    !/^0:[a-fA-F0-9]{64}$/.test(value.from) || !Number.isSafeInteger(value.validUntil) ||
    Number(value.validUntil) <= Date.now() / 1000 || Number(value.validUntil) > Date.now() / 1000 + 3600 ||
    !Array.isArray(value.messages) || value.messages.length !== (serviced ? 2 : 1)) throw new TreasuryError("UNAVAILABLE");
  const message: unknown = value.messages[0];
  if (!record(message) || typeof message.address !== "string" || !/^EQ[A-Za-z0-9_-]{46}$/.test(message.address) ||
    (phase !== "deposit" && message.address !== trialRoot) || !units(message.amount) || BigInt(message.amount) <= 0n ||
    typeof message.payload !== "string" || message.payload.length < 16 || message.payload.length > 20_000 ||
    !/^[A-Za-z0-9+/]+={0,2}$/.test(message.payload)) throw new TreasuryError("UNAVAILABLE");
  const messages: TreasuryWalletRequest["messages"] = [{ address: message.address, amount: message.amount, payload: message.payload }];
  if (serviced) {
    const advance: unknown = value.messages[1];
    if (!units(serviceAdvanceNano) || BigInt(serviceAdvanceNano) <= 0n || !record(advance) ||
      typeof advance.address !== "string" || !/^UQ[A-Za-z0-9_-]{46}$/.test(advance.address) ||
      advance.amount !== serviceAdvanceNano || typeof advance.payload !== "string" ||
      advance.payload.length < 16 || advance.payload.length > 1024 || !/^[A-Za-z0-9+/]+={0,2}$/.test(advance.payload))
      throw new TreasuryError("UNAVAILABLE");
    messages.push({ address: advance.address, amount: serviceAdvanceNano, payload: advance.payload });
  }
  return { network: "-239", from: value.from, validUntil: Number(value.validUntil), messages };
}

function purchaseStep(value: unknown, requestId: string): TreasuryPurchaseStep {
  if (!record(value) || value.requestId !== requestId) throw new TreasuryError("UNAVAILABLE");
  const phase = value.phase;
  if ((phase === "confirming-reserve" || phase === "reserved") && value.preparation !== undefined) {
    const p = value.preparation;
    if (!record(p) || !["reserving", "terms", "wallet-check", "closing"].includes(String(p.stage)) ||
      !Number.isSafeInteger(p.startedAt) || Number(p.startedAt) <= 0 || Number(p.startedAt) > Date.now() / 1000 + 60)
      throw new TreasuryError("UNAVAILABLE");
    return { phase, requestId, preparation: { stage: p.stage as "reserving" | "terms" | "wallet-check" | "closing", startedAt: Number(p.startedAt) } };
  }
  if (phase === "reserve" || phase === "arm" || phase === "cancel")
    return { phase, requestId, walletRequest: walletRequest(value.walletRequest, phase) };
  if (phase === "deposit") {
    if (!units(value.grimUnits) || BigInt(value.grimUnits) <= 0n) throw new TreasuryError("UNAVAILABLE");
    if (value.serviceAdvanceNano !== undefined && (!units(value.serviceAdvanceNano) || BigInt(value.serviceAdvanceNano) <= 0n))
      throw new TreasuryError("UNAVAILABLE");
    return { phase, requestId, grimUnits: value.grimUnits,
      ...(value.serviceAdvanceNano === undefined ? {} : { serviceAdvanceNano: value.serviceAdvanceNano }),
      walletRequest: walletRequest(value.walletRequest, phase, value.serviceAdvanceNano) };
  }
  if (phase === "need-grim") {
    if (!units(value.grimUnits) || BigInt(value.grimUnits) <= 0n) throw new TreasuryError("UNAVAILABLE");
    return { phase, requestId, grimUnits: value.grimUnits };
  }
  if (phase === "position") {
    if (!units(value.ticket) || BigInt(value.ticket) <= 0n || typeof value.rewardsPending !== "boolean")
      throw new TreasuryError("UNAVAILABLE");
    return { phase, requestId, ticket: value.ticket, rewardsPending: value.rewardsPending };
  }
  if (phase === "expired") {
    if (typeof value.restartable !== "boolean") throw new TreasuryError("UNAVAILABLE");
    return { phase, requestId, restartable: value.restartable };
  }
  if (phase === "not-started" || phase === "confirming-reserve" || phase === "reserved" || phase === "confirming-deposit" || phase === "cancelled")
    return { phase, requestId };
  throw new TreasuryError("UNAVAILABLE");
}

/** Build-pinned environment and server-owned purchase state only. */
export class LiveTreasuryService implements TreasuryPort {
  readonly #overview: TreasuryOverviewPort;
  constructor(overview: TreasuryOverviewPort) { this.#overview = overview; }

  async read(): Promise<TreasurySnapshot> {
    const response = await this.#overview.read();
    if (!record(response) || response.mode !== TREASURY_ENVIRONMENT || response.network !== "-239" ||
      (TREASURY_ENVIRONMENT === "production" && response.rootAddress !== TREASURY_PRODUCTION_ROOT) ||
      typeof response.purchasesEnabled !== "boolean" || typeof response.serverNow !== "string" ||
      !Number.isFinite(Date.parse(response.serverNow)) || !units(response.rewardAvailableMicros) ||
      !units(response.grimAvailableNano) || !Array.isArray(response.plans) || response.plans.length !== 3 ||
      (response.purchasesEnabled && (!Array.isArray(response.positions) || !Array.isArray(response.payments))) ||
      ![null, undefined].includes(response.wallet as null | undefined) && !record(response.wallet)) {
      throw new TreasuryError("UNAVAILABLE");
    }
    const wallet = response.wallet;
    if (wallet !== null && wallet !== undefined && (!record(wallet) || typeof wallet.addressUq !== "string" ||
      !/^UQ[A-Za-z0-9_-]{46}$/.test(wallet.addressUq))) throw new TreasuryError("UNAVAILABLE");
    const parsePlan = (value: unknown) => {
      if (!record(value) || typeof value.id !== "string" || !/^[1-9][0-9]{0,8}$/.test(value.id) ||
        !units(value.nominalMicros) || !Number.isSafeInteger(value.durationSeconds) || Number(value.durationSeconds) <= 0 ||
        !basisPoints(value.initialBps) || !basisPoints(value.mentorBps) || !basisPoints(value.protocolBps) ||
        typeof value.available !== "boolean") throw new TreasuryError("UNAVAILABLE");
      return { id: value.id, nominalMicros: value.nominalMicros, durationSeconds: Number(value.durationSeconds),
        initialBps: value.initialBps, mentorBps: value.mentorBps, protocolBps: value.protocolBps,
        available: value.available,
        ...(units(value.indicativeGrimNano) && BigInt(value.indicativeGrimNano) > 0n &&
          typeof value.indicativePriceAt === "string" && Number.isFinite(Date.parse(value.indicativePriceAt)) &&
          Date.parse(value.indicativePriceAt) <= Date.parse(response.serverNow as string) &&
          Date.parse(response.serverNow as string) - Date.parse(value.indicativePriceAt) <= 90_000
          ? { indicativeGrimNano: value.indicativeGrimNano, indicativePriceAt: value.indicativePriceAt } : {}) };
    };
    const plans = response.plans.map(parsePlan);
    if (new Set(plans.map(p => p.id)).size !== plans.length) throw new TreasuryError("UNAVAILABLE");
    const rawPositions: unknown[] = Array.isArray(response.positions) ? response.positions : [];
    const rawPayments: unknown[] = Array.isArray(response.payments) ? response.payments : [];
    if (rawPositions.length > 50 || rawPayments.length > 100) throw new TreasuryError("UNAVAILABLE");
    const positions = rawPositions.map(value => {
      if (!record(value) || !units(value.id) || !record(value.plan) ||
        !units(value.depositedGrimNano) || !units(value.initialMicros) ||
        typeof value.openedAt !== "string" || !Number.isFinite(Date.parse(value.openedAt)) ||
        typeof value.maturesAt !== "string" || !Number.isFinite(Date.parse(value.maturesAt)) ||
        (value.initialPaidAt !== null && (typeof value.initialPaidAt !== "string" || !Number.isFinite(Date.parse(value.initialPaidAt)))) ||
        (value.principalPaidAt !== null && (typeof value.principalPaidAt !== "string" || !Number.isFinite(Date.parse(value.principalPaidAt)))) ||
        typeof value.walletLabel !== "string" || !/^UQ[A-Za-z0-9_-]{46}$/.test(value.walletLabel) ||
        !["confirming", "active", "settling", "completed"].includes(String(value.status)))
        throw new TreasuryError("UNAVAILABLE");
      return { id: value.id, plan: parsePlan(value.plan), depositedGrimNano: value.depositedGrimNano,
        initialMicros: value.initialMicros, openedAt: value.openedAt, maturesAt: value.maturesAt,
        initialPaidAt: value.initialPaidAt, principalPaidAt: value.principalPaidAt,
        walletLabel: value.walletLabel, status: value.status as "confirming" | "active" | "settling" | "completed" };
    });
    if (new Set(positions.map(p => p.id)).size !== positions.length) throw new TreasuryError("UNAVAILABLE");
    const payments = rawPayments.map(value => {
      if (!record(value) || typeof value.id !== "string" || !/^[1-9][0-9]{0,77}:[13]$/.test(value.id) ||
        !units(value.positionId) || !["initial", "principal"].includes(String(value.kind)) ||
        !units(value.usdtMicros) || !units(value.gramNano) ||
        typeof value.paidAt !== "string" || !Number.isFinite(Date.parse(value.paidAt)))
        throw new TreasuryError("UNAVAILABLE");
      return { id: value.id, positionId: value.positionId, kind: value.kind as "initial" | "principal",
        usdtMicros: value.usdtMicros, gramNano: value.gramNano, paidAt: value.paidAt };
    });
    if (new Set(payments.map(p => p.id)).size !== payments.length ||
      payments.some(p => !positions.some(position => position.id === p.positionId))) throw new TreasuryError("UNAVAILABLE");
    return { mode: "live", purchasesEnabled: response.purchasesEnabled, serverNow: response.serverNow,
      revision: Date.parse(response.serverNow), plans, rewardAvailableMicros: response.rewardAvailableMicros,
      grimAvailableNano: response.grimAvailableNano,
      walletLabel: record(wallet) ? String(wallet.addressUq) : "", positions, payments };
  }

  async quote(_planId: string): Promise<never> { throw new TreasuryError("UNAVAILABLE"); }
  async open(_quoteId: string, _requestId: string): Promise<never> { throw new TreasuryError("UNAVAILABLE"); }
  async startPurchase(requestId: string, planId: string): Promise<TreasuryPurchaseStep> {
    if (!this.#overview.startPurchase || !requestIdPattern.test(requestId) || !/^[1-9][0-9]{0,8}$/.test(planId))
      throw new TreasuryError("UNAVAILABLE");
    return purchaseStep(await this.#overview.startPurchase(requestId, Number(planId)), requestId);
  }
  async purchaseStatus(requestId: string): Promise<TreasuryPurchaseStep> {
    if (!this.#overview.purchaseStatus || !requestIdPattern.test(requestId)) throw new TreasuryError("UNAVAILABLE");
    return purchaseStep(await this.#overview.purchaseStatus(requestId), requestId);
  }
  watchPurchase(requestId: string, onChange: () => void, onState: (state: "live" | "reconnecting") => void) {
    if (!this.#overview.watchPurchase || !requestIdPattern.test(requestId)) {
      onState("reconnecting"); return () => undefined;
    }
    return this.#overview.watchPurchase(requestId, onChange, onState);
  }
  subscribe(listener: (state: TreasurySnapshot) => void): () => void {
    if (!this.#overview.watchOverview) return () => undefined;
    let stopped = false, reading = false, again = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const refresh = async () => {
      if (stopped) return;
      if (reading) { again = true; return; }
      clearTimeout(retry); retry = undefined; reading = true; again = false;
      try {
        const snapshot = await this.read();
        if (!stopped) listener(snapshot);
      } catch {
        // Recover a failed read even if the next blockchain event is far away.
        if (!stopped) retry = setTimeout(() => { void refresh(); }, 5000);
      } finally {
        reading = false;
        if (again && !stopped) { again = false; void refresh(); }
      }
    };
    const stop = this.#overview.watchOverview(() => { void refresh(); }, state => {
      // The SSE helper refreshes on ready/reconnect. No steady-state polling.
      if (state === "reconnecting" && !stopped && !retry)
        retry = setTimeout(() => { retry = undefined; void refresh(); }, 15000);
    });
    return () => { stopped = true; clearTimeout(retry); stop(); };
  }
}
